/**
 * LineItemTable — responsive line items display.
 * Desktop: table layout. Mobile: stacked card-style rows.
 *
 * Note: amounts on PublicInvoiceView are in dollars (decimal), not cents.
 * We format them directly with Intl.NumberFormat.
 */

import type { PublicInvoiceLineItem } from '@aelita/shared';
import { formatAmount } from './formatAmount';

interface LineItemTableProps {
  lineItems: PublicInvoiceLineItem[];
}

const BLANK_DESCRIPTION_FALLBACK = 'Development work';

/**
 * Render the hours × rate subtitle for a line item.
 * Returns null when either hours or rateSnapshot is absent — no orphan text.
 */
function LineItemSubtitle({
  hours,
  rateSnapshot,
}: {
  hours?: number;
  rateSnapshot?: number;
}): React.ReactElement | null {
  if (hours == null || rateSnapshot == null) return null;
  return (
    <span className="block text-xs text-gray-400 mt-0.5">
      {hours.toFixed(2)} hrs × {formatAmount(rateSnapshot)}
    </span>
  );
}

function displayDescription(description: string): string {
  return description.trim() === '' ? BLANK_DESCRIPTION_FALLBACK : description;
}

export function LineItemTable({ lineItems }: LineItemTableProps) {
  if (lineItems.length === 0) {
    return <p className="text-sm text-gray-400 italic py-4 text-center">No line items</p>;
  }

  return (
    <>
      {/* Desktop table
          table-fixed: forces the table to honour w-full instead of expanding to
          fit content (the auto-layout default). Without this, a long description
          makes the table wider than the card and pushes the amount column past
          the card's right border. With table-fixed, the browser divides the
          available width between columns using the widths we declare, and text
          wraps rather than overflowing. */}
      <table className="hidden sm:table w-full table-fixed text-sm" aria-label="Invoice line items">
        <thead>
          <tr className="border-b border-gray-100 dark:border-gray-800">
            <th className="text-left font-medium text-gray-500 dark:text-gray-400 pb-2 pr-4">Description</th>
            {/* w-28 reserves 112 px for the amount column — wide enough for "$12,000.00"
                at this font size. The description column gets the remaining width. */}
            <th className="w-28 text-right font-medium text-gray-500 dark:text-gray-400 pb-2">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
          {lineItems.map((item, idx) => (
            <tr key={idx}>
              {/* break-words lets long descriptions wrap within the constrained column
                  width rather than forcing the row to grow horizontally. */}
              <td className="py-3 pr-4 text-gray-700 dark:text-gray-200 break-words">
                {displayDescription(item.description)}
                <LineItemSubtitle hours={item.hours} rateSnapshot={item.rateSnapshot} />
              </td>
              <td className="w-28 py-3 text-right text-gray-900 dark:text-gray-100 tabular-nums align-top">
                {formatAmount(item.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile stacked list */}
      <ul className="sm:hidden space-y-3" aria-label="Invoice line items">
        {lineItems.map((item, idx) => (
          <li key={idx} className="flex justify-between items-start gap-3">
            <span className="text-sm text-gray-700 dark:text-gray-200 flex-1">
              {displayDescription(item.description)}
              <LineItemSubtitle hours={item.hours} rateSnapshot={item.rateSnapshot} />
            </span>
            <span className="text-sm text-gray-900 dark:text-gray-100 tabular-nums shrink-0">
              {formatAmount(item.amount)}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}
